import { useState, useEffect, useCallback } from "react";
import {
  Card,
  Table,
  TableRow,
  TableBody,
  TableCell,
  Typography,
  TableContainer,
  TableHead,
  Box,
  Chip,
  CircularProgress,
  Button,
} from "@mui/material";
import Scrollbar from "../scrollbar";
import { get } from "src/utils/networkUtils";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import {
  setVersusGames,
  appendVersusGames,
  clearVersusGames,
} from "src/store/slices/versusGamesSlice";
import { TABLE_HEAD } from "src/utils/versusUserGamesTableHead";
import {
  formatGameMode,
  formatEntry,
  formatPrize,
  formatRecord,
  getGameModeColor,
} from "src/utils/versusUtils";

export default function VersusUserGamesTable({ userId, isActive }) {
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [offset, setOffset] = useState(0);
  const [limit] = useState(50);

  const navigate = useNavigate();

  const dispatch = useDispatch();

  const fetchGames = useCallback(
    async (currentOffset = 0, isInitial = false) => {
      if (!userId) return;

      setLoading((prevLoading) => {
        if (prevLoading) return prevLoading;

        (async () => {
          try {
            const response = await get(
              `/admin/versus/users/${userId}/games?offset=${currentOffset}&limit=${limit}`
            );
            const newGames = response.versusGames || [];

            if (isInitial) {
              setGames(newGames);
              dispatch(setVersusGames(newGames));
            } else {
              setGames((prev) => [...prev, ...newGames]);
              dispatch(appendVersusGames(newGames));
            }

            setHasMore(newGames.length === limit);
            setOffset(currentOffset + limit);
          } catch (error) {
            console.error("Error fetching user games:", error);
            if (isInitial) {
              setGames([]);
              dispatch(clearVersusGames());
            }
          } finally {
            setLoading(false);
          }
        })();

        return true;
      });
    },
    [userId, limit, dispatch]
  );

  const loadMore = useCallback(() => {
    if (!hasMore) return;
    fetchGames(offset);
  }, [hasMore, fetchGames, offset]);

  useEffect(() => {
    if (isActive && userId) {
      setGames([]);
      setOffset(0);
      setHasMore(true);
      fetchGames(0, true);
    }
  }, [isActive, userId, fetchGames]);

  useEffect(() => {
    const handleScroll = () => {
      const scrollContainer = document.querySelector(".MuiTableContainer-root");
      if (!scrollContainer || loading || !hasMore) return;

      const { scrollTop, scrollHeight, clientHeight } = scrollContainer;
      if (scrollTop + clientHeight >= scrollHeight - 5) {
        loadMore();
      }
    };

    const scrollContainer = document.querySelector(".MuiTableContainer-root");
    if (scrollContainer) {
      scrollContainer.addEventListener("scroll", handleScroll);
      return () => scrollContainer.removeEventListener("scroll", handleScroll);
    }
  }, [loading, hasMore, loadMore]);

  if (!userId) {
    return (
      <Card>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            py: 3,
          }}
        >
          <Typography variant="body2" color="text.secondary">
            No user selected
          </Typography>
        </Box>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <Scrollbar>
          <TableContainer
            sx={{ minWidth: 800, maxHeight: "100vh", overflow: "auto" }}
          >
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  {TABLE_HEAD.map((headCell) => (
                    <TableCell
                      key={headCell.id}
                      align={headCell.alignRight ? "right" : "left"}
                    >
                      {headCell.label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {games.map((row) => {
                  const {
                    versusGameId,
                    gameMode,
                    opponentFpts,
                    userFpts,
                    isWin,
                    opponentUserId,
                    userWins,
                    userLosses,
                    opponentUsername,
                    gameDetails,
                  } = row;

                  return (
                    <TableRow hover key={versusGameId} tabIndex={-1}>
                      <TableCell align="left">
                        <Typography variant="body2" noWrap>
                          {formatRecord(userWins, userLosses)}
                        </Typography>
                      </TableCell>

                      <TableCell align="left">
                        <Typography variant="body2" noWrap>
                          {userFpts !== null ? userFpts.toFixed(2) : "N/A"}
                        </Typography>
                      </TableCell>

                      <TableCell align="left">
                        <Typography variant="body1" noWrap>
                          <span
                            style={{
                              fontWeight:
                                gameDetails?.userScore >
                                gameDetails?.opponentScore
                                  ? "bold"
                                  : "normal",
                            }}
                          >
                            {gameDetails?.userScore || 0}
                          </span>
                          {" - "}
                          <span
                            style={{
                              fontWeight:
                                gameDetails?.opponentScore >
                                gameDetails?.userScore
                                  ? "bold"
                                  : "normal",
                            }}
                          >
                            {gameDetails?.opponentScore || 0}
                          </span>
                        </Typography>
                      </TableCell>

                      <TableCell align="left">
                        <Typography variant="body2" noWrap>
                          {opponentFpts !== null
                            ? opponentFpts.toFixed(2)
                            : "N/A"}
                        </Typography>
                      </TableCell>

                      <TableCell align="left">
                        <Typography variant="body2" noWrap>
                          {opponentUserId !== null ? (
                            <a
                              href={`/dashboard/profile?user_id=${opponentUserId}`}
                            >
                              {opponentUsername || opponentUserId}
                            </a>
                          ) : (
                            "N/A"
                          )}
                        </Typography>
                      </TableCell>

                      <TableCell align="left">
                        <Chip
                          label={formatGameMode(gameMode)}
                          color={getGameModeColor(gameMode)}
                          size="small"
                        />
                      </TableCell>

                      <TableCell align="left">
                        <Typography variant="body2" noWrap>
                          {formatEntry(gameMode)}
                        </Typography>
                      </TableCell>

                      <TableCell align="left">
                        <Typography variant="body2" noWrap>
                          {isWin ? formatPrize(gameMode) : "--"}
                        </Typography>
                      </TableCell>

                      <TableCell align="left">
                        <Chip
                          label={isWin ? "Won" : "Lost"}
                          color={isWin ? "success" : "error"}
                          size="small"
                        />
                      </TableCell>

                      <TableCell align="left">
                        <Typography variant="body2" noWrap>
                          {new Date(row.createdAt).toLocaleDateString("en-US", {
                            year: "numeric",
                            month: "2-digit",
                            day: "2-digit",
                            hour: "2-digit",
                            minute: "2-digit",
                            timeZone: "America/New_York",
                          })}
                        </Typography>
                      </TableCell>
                      <TableCell align="left">
                        <Button
                          variant="text"
                          size="small"
                          sx={{
                            p: 0,
                            minWidth: "auto",
                            textTransform: "none",
                          }}
                          onClick={() =>
                            navigate(
                              `/dashboard/versus-game-details/${versusGameId}`
                            )
                          }
                        >
                          Details
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
                {loading && (
                  <TableRow>
                    <TableCell align="center" colSpan={10}>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          py: 3,
                        }}
                      >
                        <CircularProgress size={24} sx={{ mr: 2 }} />
                        <Typography variant="body2">
                          Loading more games...
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                )}
                {!hasMore && games.length > 0 && (
                  <TableRow>
                    <TableCell align="center" colSpan={10}>
                      <Typography
                        variant="body2"
                        sx={{ py: 2, color: "text.secondary" }}
                      >
                        No more games to load
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
                {!loading && games.length === 0 && (
                  <TableRow>
                    <TableCell align="center" colSpan={10}>
                      <Typography
                        variant="body2"
                        sx={{ py: 3, color: "text.secondary" }}
                      >
                        No games found for this user
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Scrollbar>
      </Card>
    </>
  );
}
